import { Field } from "@rosc/ui/v32/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rosc/ui/v32/select";
import type { JSX } from "react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  READING_LEVELS,
  type ReadingLevel,
  readReadingLevel,
  rememberReadingLevel,
} from "@/lib/reading-level";
import {
  isSummaryLanguage,
  readSummaryLanguage,
  rememberSummaryLanguage,
  SUMMARY_LANGUAGE_NATIVE,
  SUMMARY_LANGUAGES,
  type SummaryLanguage,
} from "@/lib/summary-language";
import { cn } from "@/lib/utils";
import { useVisitScope } from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";
import { useSettingsOptions } from "@/store/settingsOptions.store";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // "create" from the Create menu; "regenerate" from an existing summary,
  // which writes a new summary next to it.
  mode: "create" | "regenerate";
  onConfirm: (level: ReadingLevel, language: SummaryLanguage) => void;
}

// The account's note language code (Settings → Transcription), when loaded:
// the summary language to start on when none was chosen yet.
const noteLanguageCode = (): string | null => {
  const id =
    useAuthStore.getState().userData?.userPreference?.transcriptionLanguageId;
  return (
    useSettingsOptions
      .getState()
      .transcriptionLanguages.find((language) => language.id === id)?.code ??
    null
  );
};

// Ontario RFS: patient summaries tailored to the patient's reading level.
// Starts on the level this account chose last (Standard the first time).
// The output language sits under the level: the last one chosen, else the
// account's note language when it is offered, else English.
export const ReadingLevelDialog = ({
  open,
  onOpenChange,
  mode,
  onConfirm,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const account = useVisitScope()?.accountPublicId;
  const [level, setLevel] = useState<ReadingLevel>(() =>
    readReadingLevel(account),
  );
  const [language, setLanguage] = useState<SummaryLanguage>(() =>
    readSummaryLanguage(account, noteLanguageCode()),
  );
  const name = useId();
  const languageId = useId();
  useEffect(() => {
    if (!open) return;
    setLevel(readReadingLevel(account));
    setLanguage(readSummaryLanguage(account, noteLanguageCode()));
  }, [open, account]);

  const confirm = (): void => {
    rememberReadingLevel(account, level);
    rememberSummaryLanguage(account, language);
    onOpenChange(false);
    onConfirm(level, language);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The shared close button, enlarged to a 44px touch target.
        className="w-[calc(100%-2rem)] max-w-md gap-5 rounded-2xl p-5 sm:p-6 [&>button.absolute]:right-2 [&>button.absolute]:top-2 [&>button.absolute]:flex [&>button.absolute]:size-11 [&>button.absolute]:items-center [&>button.absolute]:justify-center"
        data-testid="reading-level-dialog"
        // Start on the selected level, not the first one.
        onOpenAutoFocus={(event) => {
          const checked = (
            event.currentTarget as HTMLElement | null
          )?.querySelector<HTMLInputElement>("input:checked");
          if (checked) {
            event.preventDefault();
            checked.focus({ preventScroll: true });
          }
        }}
      >
        <DialogHeader className="space-y-2 pr-6 text-left">
          <DialogTitle>
            {mode === "create"
              ? t("visit.workspace.readingLevel.createTitle")
              : t("visit.workspace.readingLevel.regenerateTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t("visit.workspace.readingLevel.description")}
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">
            {t("visit.workspace.readingLevel.label")}
          </legend>
          {READING_LEVELS.map((value) => (
            <label
              key={value}
              className={cn(
                "flex min-h-14 cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors hover:bg-accent has-focus-visible:ring-2 has-focus-visible:ring-ring/60",
                level === value && "border-primary bg-primary/5",
              )}
            >
              <input
                checked={level === value}
                className="mt-1 size-4 shrink-0 accent-primary focus-visible:outline-hidden"
                name={name}
                type="radio"
                value={value}
                onChange={() => {
                  setLevel(value);
                }}
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium">
                  {t(`visit.workspace.readingLevel.levels.${value}.name`)}
                  <span className="text-xs font-normal text-muted-foreground">
                    {t(`visit.workspace.readingLevel.levels.${value}.grade`)}
                  </span>
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  {t(`visit.workspace.readingLevel.levels.${value}.hint`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <Field
          hint={t("visit.workspace.summaryLanguage.hint")}
          htmlFor={languageId}
          label={t("visit.workspace.summaryLanguage.label")}
        >
          <Select
            value={language}
            onValueChange={(value) => {
              if (isSummaryLanguage(value)) setLanguage(value);
            }}
          >
            <SelectTrigger
              data-testid="summary-language-select"
              id={languageId}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent data-testid="summary-language-options">
              {SUMMARY_LANGUAGES.map((value) => {
                const label = t(
                  `visit.workspace.summaryLanguage.languages.${value}`,
                );
                const native = SUMMARY_LANGUAGE_NATIVE[value];
                return (
                  <SelectItem key={value} className="min-h-11" value={value}>
                    {label}
                    {native !== label && (
                      <span className="ml-2 text-v3-text-3">{native}</span>
                    )}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </Field>
        <DialogFooter className="gap-2 sm:items-center sm:gap-0">
          <Button
            className="h-11 lg:h-10"
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            className="h-11 lg:h-10"
            data-testid="reading-level-confirm"
            type="button"
            onClick={confirm}
          >
            {mode === "create"
              ? t("visit.workspace.readingLevel.create")
              : t("visit.workspace.readingLevel.regenerate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
